import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Stack, Typography } from '@mui/material'
import PauseRoundedIcon from '@mui/icons-material/PauseRounded'
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded'
import MosSmartDialog from '../../../common/MosSmartDialog'
import WorkerRequiredButton from '../../../common/WorkerRequiredButton'
import createDialogTitle from '../../../../../generic/utilities/dialogUtil'
import BatchBadge from '../../../common/batch/BatchBadge'
import { formatBatchLabel } from '../../../common/batch/batchLabel'

/**
 * Historia 120 — Bloque 13 (Jupiter, paquete P7): `features/S143-detalle-y-acciones-de-lote-jupiter.feature`
 * (S144, S145, S148, S149) + `features/S206-preparacion-y-reporte-jupiter.feature` (S207) —
 * **REESCRITO por la ENMIENDA 06** (G3-10),
 * `features/S233-ui-previsualizacion-y-barra-de-lote-jupiter.feature` (S237, S238).
 *
 * 🔴 EL DEFECTO QUE EL PO VIO, con el producto delante: con **dos lotes activos** en la misma máquina
 * este componente pintaba **DOS FILAS IDÉNTICAS** de *Pause batch* / *Finish batch*, sin nada que
 * dijera a qué lote pertenecía cada una. Sus palabras: *"cuando hay más de un grupo no se diferencia
 * el grupo; lo suyo sería que las opciones de finish batch sean del grupo del job que está
 * seleccionado"*.
 *
 * ⇒ Renders EXACTLY TWO buttons — *Pause batch* and *Finish batch* — **para UN SOLO lote: el del job
 * SELECCIONADO** (`useBatchActions.selectedBatchGroup`), encima de ellos la etiqueta que lo
 * identifica. **Sin lote en el job seleccionado ⇒ no se pinta nada** (S145), aunque en la máquina haya
 * otros jobs que sí lo tengan. ⚠️ Consecuencia declarada y aceptada por el PO al elegir: para actuar
 * sobre OTRO lote hay que seleccionar antes uno de sus jobs.
 *
 * ⚠️ There is no third button here and never will be: *Report all jobs* was retired by the enmienda 02
 * (G3-4/RN37.1) before this widget was built, and reporting stays the per-job flow the container
 * already has. Tampoco hay un selector de lote: se descartó por escrito (G3-10).
 *
 * 🔒 **S238/D-SP24 — la etiqueta se DERIVA, no se pide:** el recuento y las órdenes salen del `batchId`
 * que Sun ya publica por job (**F3**); este componente no llama a nada. La parte traducible es **una
 * sola clave con interpolación**, `Batch of {{count}} jobs` — ❌ descartado componerla concatenando
 * claves existentes, porque **el orden no se sostiene en los 8 idiomas**. Las órdenes de fabricación se
 * anexan sin traducir.
 *
 * The existing i18n keys `Pause batch`/`Finish batch` are reused verbatim. A group's
 * `disabled`/`disabledTooltip` come straight from a PRE-EXISTING gate (S148) — this component never
 * decides that on its own, it only renders what `useBatchActions` resolved.
 */
const BatchActionsBar = ({ group, badge = null, onPauseBatch, onOpenFinishBatch, t_ = (key) => key }) => {
  // 🔒 El hook va ANTES del retorno temprano: sin lote no se pinta nada, pero el orden de hooks no
  // puede depender de eso.
  const [pauseConfirmOpen, setPauseConfirmOpen] = useState(false)

  if (!group?.batchId) {
    return null
  }

  // La MISMA etiqueta que el tooltip del distintivo de la lista (S239) y que el cuerpo de la
  // confirmación (D-SP25): un solo sitio la compone.
  const label = formatBatchLabel(group, t_)

  return (
    <>
      {/* S246 -- el pie del widget: separado del área de trabajo del job y con peso SECUNDARIO
          (`variant='text'`, `size='small'`) frente a las de *Pausar* y *Finalizar* del job, que son las de uso
          frecuente y conservan el suyo. Secundario NO es invisible: se ven y se alcanzan sin desplegar
          nada -- esconderlas en un menú se descartó por escrito. */}
      <Stack
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ mt: 1, pt: 1, borderTop: 1, borderColor: 'divider', flexWrap: 'wrap', rowGap: 0.5 }}
      >
        {/* S246/OBS-N17: la etiqueta NO es decoración -- con seis colores dos lotes pueden coincidir,
            y es ella la que dice de QUÉ lote es esta pareja. */}
        <BatchBadge badge={badge} />
        <Typography variant='caption' color='text.secondary' sx={{ flexGrow: 1, minWidth: 0 }}>{label}</Typography>
        {/* 🔴 S261 (ENMIENDA 11, G3-15) -- LA MISMA PUERTA QUE EL RESTO DE MOS. `Start batch` y las
            acciones individuales usan `WorkerRequiredButton` desde hace tres enmiendas; esta pareja era
            la ÚNICA acción de lote sin ella, y `batchContract.js` valida la lista de jobs vacía pero NO
            el operario ⇒ sin operario seleccionado el gesto salía con `workerId` AUSENTE, y es el que
            toca N jobs a la vez. Sin operario, el componente lo PIDE y NO ejecuta el `onClick`: la
            confirmación de la pausa ni se abre, y no sale ninguna petición. */}
        <WorkerRequiredButton
          size='small'
          variant='text'
          color='inherit'
          startIcon={<PauseRoundedIcon />}
          disabled={group.disabled}
          title={group.disabledTooltip}
          onClick={() => setPauseConfirmOpen(true)}
        >
          {t_('Pause batch')}
        </WorkerRequiredButton>
        <WorkerRequiredButton
          size='small'
          variant='text'
          color='inherit'
          startIcon={<DoneAllRoundedIcon />}
          disabled={group.disabled}
          title={group.disabledTooltip}
          onClick={() => onOpenFinishBatch(group.batchId)}
        >
          {t_('Finish batch')}
        </WorkerRequiredButton>
      </Stack>

      {/* 🔴 S146/S247/D-SP25 -- PULSAR NO ENVÍA NADA. Pausar un lote entero por error es peor que
          pausar un job, así que el gesto sale al CONFIRMAR y no al pulsar. La confirmación dice lo
          único que hace falta saber antes: QUÉ lote y CUÁNTOS jobs.
          🔒 CERO claves nuevas: título `Pause batch`, cuerpo la etiqueta del lote, botones `Cancel` y
          `Confirm` -- las cuatro ya en los ocho idiomas.
          ⚠️ *Finalizar en bloque* NO gana una segunda confirmación: su previsualización (N4) ya lo es. */}
      <MosSmartDialog
        title={createDialogTitle({ color: 'warning', title: t_('Pause batch'), icon: PauseRoundedIcon })}
        setOpen={pauseConfirmOpen}
        message={label}
        cancel
        accept
        cancelLabel='Cancel'
        acceptLabel='Confirm'
        cancelCallback={() => setPauseConfirmOpen(false)}
        acceptCallback={() => {
          setPauseConfirmOpen(false)
          onPauseBatch(group.batchId)
        }}
      />
    </>
  )
}

BatchActionsBar.propTypes = {
  badge: PropTypes.shape({ color: PropTypes.string, label: PropTypes.string }),
  group: PropTypes.shape({
    batchId: PropTypes.string.isRequired,
    jobs: PropTypes.array,
    fabricationOrderCodes: PropTypes.arrayOf(PropTypes.string),
    disabled: PropTypes.bool,
    disabledTooltip: PropTypes.string
  }),
  onPauseBatch: PropTypes.func.isRequired,
  onOpenFinishBatch: PropTypes.func.isRequired,
  t_: PropTypes.func
}

export default BatchActionsBar
